import React from "react";
import { Form } from "react-bootstrap";
import { Building2 } from "lucide-react";
import { useBranch } from "../../contexts/BranchContext";

/**
 * "Available at" — which branches an entity (course, assessment) is offered at.
 *
 * Renders nothing at all unless the institute is on a multi-branch plan AND has
 * more than one branch. A single-branch institute has no allocation to make, and
 * showing the control there would be a decision with exactly one answer.
 *
 * The model behind it is a two-value scope plus a list, not just a list:
 * "available everywhere" has to be its own stored value so the backend can
 * index it. An empty list means "not chosen yet", never "everywhere".
 *
 * Props:
 *   scope      'all' | 'branches'
 *   branchIds  string[]  (only meaningful when scope === 'branches')
 *   onChange({ branchScope, branchIds })
 *   disabled   true for a branch-restricted admin — allocation is an
 *              institute-level catalogue decision and the API refuses it
 *   label / hint  wording overrides for non-course entities
 *
 * Converted to utilities in Phase 5; branch-allocation.scss deleted in the same
 * commit (TOKEN_CONTRACT §6.1). The one rule worth reading twice is the grid:
 * `repeat(auto-fill, minmax(170px, 1fr))` is what keeps ten branches inside the
 * 1280x720 admin viewport instead of pushing the rest of the form below the fold.
 */
const BranchAllocationField = ({
  scope = "all",
  branchIds = [],
  onChange,
  disabled = false,
  label = "Available at",
  hint = "Choose which branches offer this course. Batches can only run where it is offered.",
}) => {
  const { branches, isMultiBranch } = useBranch();

  const active = (branches || []).filter((b) => b.isActive);
  if (!isMultiBranch || active.length <= 1) return null;

  const selected = new Set((branchIds || []).map(String));

  const setScope = (next) => {
    onChange({
      branchScope: next,
      // Keep the picked list when flipping to "all" so toggling back does not
      // wipe a selection the user already made.
      branchIds: branchIds || [],
    });
  };

  const toggleBranch = (id) => {
    const next = new Set(selected);
    if (next.has(String(id))) next.delete(String(id));
    else next.add(String(id));
    onChange({ branchScope: "branches", branchIds: [...next] });
  };

  return (
    <div className="tw-rounded-[10px] tw-border tw-border-line tw-bg-surface-raised tw-px-3.5 tw-py-3">
      <div className="tw-mb-2 tw-flex tw-items-center tw-gap-1.5 tw-text-content">
        <Building2 size={15} />
        <span className="tw-text-[0.85rem] tw-font-semibold">{label}</span>
      </div>

      <div className="tw-mb-1.5 tw-flex tw-flex-wrap tw-gap-[18px]">
        <Form.Check
          type="radio"
          id="branch-alloc-all"
          name="branchScope"
          label="All branches"
          checked={scope !== "branches"}
          disabled={disabled}
          onChange={() => setScope("all")}
        />
        <Form.Check
          type="radio"
          id="branch-alloc-some"
          name="branchScope"
          label="Selected branches only"
          checked={scope === "branches"}
          disabled={disabled}
          onChange={() => setScope("branches")}
        />
      </div>

      {scope === "branches" && (
        <div className="tw-mb-1 tw-mt-1.5 tw-grid tw-grid-cols-[repeat(auto-fill,minmax(170px,1fr))] tw-gap-x-4 tw-gap-y-[2px] tw-rounded-[8px] tw-bg-surface-alt tw-px-2.5 tw-py-2">
          {active.map((b) => (
            <Form.Check
              key={b._id}
              type="checkbox"
              id={`branch-alloc-${b._id}`}
              label={b.name}
              checked={selected.has(String(b._id))}
              disabled={disabled}
              onChange={() => toggleBranch(b._id)}
            />
          ))}
          {scope === "branches" && selected.size === 0 && (
            <div className="tw-col-span-full tw-mt-1 tw-text-[0.75rem] tw-text-warning-fg">
              Pick at least one branch, or switch back to “All branches”.
            </div>
          )}
        </div>
      )}

      <div className="tw-text-[0.75rem] tw-text-content-muted">
        {disabled
          ? "Only an institute administrator can change which branches offer this."
          : hint}
      </div>
    </div>
  );
};

export default BranchAllocationField;
